import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useSelectedMediaDevice } from "../hooks/useMediaDeviceSelector";
import { isError } from "../utils/is-error";
import { getLocalMedia } from "../webtrc/local-media";
import AudioLevelDetector from "./AudioLevelDetector";
import MediaDeviceSelector from "./MediaDeviceSelector";
import Stack from "./Stack";

const MediaSettingsElement = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 20px;
`;

const VideoPanel = styled.div`
  position: relative;
  width: 100%;
`;
const ErrorMessage = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
`;
const VideoPreview = styled.video`
  max-width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: contain;
  background: #000;
  border-radius: 5px;
  transform: scale(-1, 1);
`;
const SettingsPanel = styled.div`
  margin: auto 0;
`;

type Props = {
  onMediaReady?: () => void;
  onMediaError?: (err: string) => void;
};
const MediaSettings: React.FC<Props> = ({ onMediaReady, onMediaError }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [localMediaStream, setLocalMediaStream] = useState<MediaStream | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [videoDevice] = useSelectedMediaDevice("videoinput");
  const [audioDevice] = useSelectedMediaDevice("audioinput");

  useEffect(() => {
    if (localMediaStream && videoRef.current) {
      videoRef.current.srcObject = localMediaStream;
      videoRef.current.play();
      onMediaReady?.();
    }
    return () => {
      localMediaStream?.getTracks().forEach((t) => t.stop());
    };
  }, [localMediaStream]);

  useEffect(() => {
    async function updateMedia() {
      try {
        if (localMediaStream) {
          localMediaStream.getTracks().forEach((t) => t.stop());
        }
        setErrorMessage(null);
        const requested = {
          videoDeviceId: videoDevice?.deviceId,
          audioDeviceId: audioDevice?.deviceId
        };
        const localStream = await getLocalMedia(requested);
        setLocalMediaStream(localStream);
      } catch (err) {
        console.error({ err });
        if (isError(err)) {
          let message;
          switch (err.name) {
            case "NotFoundError":
            case "DevicesNotFoundError":
              message = "Camera is not found? Camera is required to join";
              break;
            case "NotReadableError":
              message = "Camera is busy. Possibly in use by another app?";
              break;
            case "SourceUnavailableError":
              message = "Your camera appears to busy";
              break;
            case "PermissionDeniedError":
            case "NotAllowedError":
            case "SecurityError":
              message = "Allow access to your camera to join";
              break;
            default:
              message = err.message;
          }
          setErrorMessage(message);
          onMediaError?.(message);
        }
      }
    }
    updateMedia();
  }, [videoDevice, audioDevice]);

  return (
    <MediaSettingsElement>
      <VideoPanel>
        <VideoPreview width={"100%"} ref={videoRef} muted />
        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
      </VideoPanel>
      <SettingsPanel>
        <Stack>
          <MediaDeviceSelector kind="videoinput" />
          <MediaDeviceSelector kind="audioinput" />
          <AudioLevelDetector stream={localMediaStream} />
        </Stack>
      </SettingsPanel>
    </MediaSettingsElement>
  );
};

export default MediaSettings;
